import { useBuilderContext } from "@Context/game/builder/builder-context";
import { ScenarioActionType } from "@Enums/index";
import useI18n from "@i18n";
import { Button, Drawer, Stack, Typography, useTheme } from "@mui/material";
import "@pixi/events";
import { Point } from "@Shared/types/ScenarioTypes";
import { forEach, isNumber } from "lodash-es";
import { useEffect } from "react";
import { getCategoryForHeight, getHeightFromFlightTime, getNearestKickHeight, getProjectileFlightTime, isHeightInCategoryRange } from "./functions/physics";
import { getPlayer, updateAction } from "./functions/player-functions";
import { getEndTime, getPointAtTime, screenToField } from "./functions/utilities";
import { PhysicsPrompt, Player, PlayerAction, ScenarioWarning, WarningSeverity } from "./models/builder-data-types";
import { BybButton } from "@Components/common/byb-ds";
import { ButtonColor } from "@Components/game/common/buttons/byb-button/config";

const WarningList = () => {
  const { state, dispatch } = useBuilderContext();
  const { i18n } = useI18n();

  const theme = useTheme();

  const checkSpeed = (
    warnings: ScenarioWarning[],
    player: Player,
    point: { position: Point },
    previousPoint: { position: Point },
    t: number,
    timeSpan: number,
    speedLimit: number
  ) => {
    const fromFieldPos = screenToField(previousPoint.position, state.field);
    const toFieldPos = screenToField(point.position, state.field);
    const distance = Math.sqrt(Math.pow(toFieldPos.x - fromFieldPos.x, 2) + Math.pow(toFieldPos.y - fromFieldPos.y, 2));
    const speed = distance / timeSpan;

    if (speed > speedLimit) {
      warnings.push({
        isForBall: false,
        severity: speed > speedLimit * 1.5 ? WarningSeverity.Critical : WarningSeverity.Moderate,
        message: i18n("builder.warning.player.speed", { name: player.name, team: String(player.team), excess: (speed - speedLimit).toFixed(2), time: t.toFixed(2) }),
        player: player.id,
        time: t,
      });

      return true;
    }

    return false;
  };

  useEffect(() => {
    const newWarnings: ScenarioWarning[] = [];
    const ballSpeedLimit = state.options.ballSpeedLimit;

    // measure the speed of the ball for passes and kicks, if speed > speedLimit, add a warning
    for (let i = 0; i < state.ballStates.length - 1; i++) {
      const ballState = state.ballStates[i];
      if (ballState.state === "passed" && isNumber(ballState.kicker) && isNumber(ballState.receiver) && isNumber(ballState.receiveTime)) {
        const kicker = getPlayer(state.clip, ballState.kicker);
        const receiver = getPlayer(state.clip, ballState.receiver);
        if (!kicker || !receiver) continue;
        const kickerPos = screenToField(getPointAtTime(kicker.points, ballState.time, kicker.beziers).position, state.field);
        const receiverPos = screenToField(getPointAtTime(receiver.points, ballState.receiveTime, receiver.beziers).position, state.field);
        const flightTime = ballState.receiveTime - ballState.time;
        const distance = Math.sqrt((receiverPos.x - kickerPos.x) ** 2 + (receiverPos.y - kickerPos.y) ** 2);
        const speed = distance / flightTime;
        if (speed > ballSpeedLimit) {
          newWarnings.push({
            severity: speed > ballSpeedLimit * 1.5 ? WarningSeverity.Critical : WarningSeverity.Moderate,
            isForBall: true,
            message: i18n("builder.warning.pass.speed", { kicker: kicker.name, receiver: receiver.name, speed: speed.toFixed(2), time: ballState.time.toFixed(2) }),
            player: ballState.kicker,
            action: ballState.actionIndex,
            time: ballState.time,
          });
        }
      }

      if (ballState.state === "kicked" && isNumber(ballState.kicker) && ballState.target && isNumber(ballState.arriveTime)) {
        const kicker = getPlayer(state.clip, ballState.kicker);
        if (!kicker) continue;
        const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers);
        const flightTime = ballState.arriveTime - ballState.time;
        const fromPos = screenToField(kickerPos.position, state.field);
        const toPos = screenToField(ballState.target, state.field);
        const distance = Math.sqrt((toPos.x - fromPos.x) ** 2 + (toPos.y - fromPos.y) ** 2);
        const speed = distance / flightTime;
        if (speed > ballSpeedLimit) {
          newWarnings.push({
            severity: speed > ballSpeedLimit * 1.5 ? WarningSeverity.Critical : WarningSeverity.Moderate,
            isForBall: true,
            message: i18n("builder.warning.kick.speed", { kicker: kicker.name, speed: speed.toFixed(2), time: ballState.time.toFixed(2) }),
          });
        }
      }
    }

    const players = Array.from(state.clip.scenario.players.values());
    const timeSpan = 0.2;
    const playerSpeedLimit = state.options.playerSpeedLimit;

    // measure the speed of players at 0.5s intervals, if their speed > speed limit, add a warning
    forEach(players, (player) => {
      let previousPoint: { position: Point } = player.points[0];
      let previousT = 0;
      const playerEndTime = getEndTime(player);

      for (let t = timeSpan; t < playerEndTime; t += timeSpan) {
        const point = getPointAtTime(player.points, t, player.beziers);
        previousT = t;

        if (checkSpeed(newWarnings, player, point, previousPoint, t, timeSpan, playerSpeedLimit)) {
          t += 1;
        }

        previousPoint = point;
      }

      checkSpeed(
        newWarnings,
        player,
        player.points[player.points.length - 1],
        previousPoint,
        playerEndTime,
        playerEndTime - previousT,
        playerSpeedLimit
      );
    });

    forEach(players, (player) => {
      player.actions.forEach((action: PlayerAction, actionIdx: number) => {
        if (action.type !== ScenarioActionType.PassTarget) return;
        if (action.height <= 0) return;

        const flightTime = action.time - action.startTime;
        if (flightTime <= 0) return;
        if (isHeightInCategoryRange(action.height, flightTime)) return;

        const category = getCategoryForHeight(action.height);
        const expectedFlightTime = getProjectileFlightTime(action.height);
        const computedHeight = getHeightFromFlightTime(flightTime);
        const nearestHeight = getNearestKickHeight(computedHeight);

        const createFixAction = () => () => {
          const prompt: PhysicsPrompt = {
            mode: "fix",
            playerId: player.id,
            actionIndex: actionIdx,
            currentHeight: action.height,
            currentFlightTime: flightTime,
            proposedHeight: computedHeight,
            proposedFlightTime: expectedFlightTime ?? flightTime,
            proposedHeightName: nearestHeight.name,
            currentHeightName: category.name,
            onConfirm: () => {},
            onConfirmAdjustTime: () => {
              if (expectedFlightTime === null) return;
              const freshPlayer = getPlayer(state.clip, player.id);
              if (!freshPlayer) return;
              const freshAction = freshPlayer.actions[actionIdx];
              if (!freshAction) return;
              const newTime = freshAction.startTime + expectedFlightTime;
              const adjusted = { ...freshAction, time: newTime, endTime: Math.max(freshAction.endTime, newTime) };
              updateAction(state, dispatch, player.id, actionIdx, adjusted);
            },
            onConfirmAdjustHeight: () => {
              const freshPlayer = getPlayer(state.clip, player.id);
              if (!freshPlayer) return;
              const freshAction = freshPlayer.actions[actionIdx];
              if (!freshAction) return;
              const adjusted = { ...freshAction, height: computedHeight };
              updateAction(state, dispatch, player.id, actionIdx, adjusted);
            },
            onCancel: () => {},
          };

          dispatch({
            time: action.startTime,
            selectedPlayer: player.id,
            selectedAction: actionIdx,
            showWarnings: false,
            physicsPrompt: prompt,
          });
        };

        newWarnings.push({
          severity: WarningSeverity.Critical,
          isForBall: true,
          warningType: "projectile",
          message: i18n("builder.physics.warning.message", {
            name: player.name,
            time: action.startTime.toFixed(2),
            height: category.name,
            requiredTime: expectedFlightTime?.toFixed(2) ?? "?",
            actualTime: flightTime.toFixed(2),
          }),
          player: player.id,
          action: actionIdx,
          time: action.startTime,
          fixAction: createFixAction(),
        });
      });
    });

    const EXTREME_HEIGHT = 12;
    const EXTREME_FLIGHT_TIME = 3;

    forEach(players, (player) => {
      player.actions.forEach((action: PlayerAction, actionIdx: number) => {
        if (action.type !== ScenarioActionType.PassTarget) return;

        const flightTime = action.time - action.startTime;

        if (action.height > EXTREME_HEIGHT) {
          newWarnings.push({
            severity: WarningSeverity.Moderate,
            isForBall: true,
            warningType: "extreme",
            message: i18n("builder.warning.pass.extreme.height", {
              name: player.name,
              time: action.startTime.toFixed(2),
              height: action.height.toFixed(1),
            }),
            player: player.id,
            action: actionIdx,
            time: action.startTime,
          });
        }

        if (flightTime > EXTREME_FLIGHT_TIME) {
          newWarnings.push({
            severity: WarningSeverity.Moderate,
            isForBall: true,
            warningType: "extreme",
            message: i18n("builder.warning.pass.extreme.time", {
              name: player.name,
              time: action.startTime.toFixed(2),
              flightTime: flightTime.toFixed(2),
            }),
            player: player.id,
            action: actionIdx,
            time: action.startTime,
          });
        }
      });
    });

    newWarnings.sort((a, b) => {
      if (a.severity !== b.severity) return b.severity - a.severity;
      return (a.time && b.time) ? a.time - b.time : 0;
    });

    dispatch({ warnings: newWarnings });
  }, [state.ballStates, state.clip.scenario.players, state.options.ballSpeedLimit, state.options.playerSpeedLimit]);

  const handleWarningClick = (warning: ScenarioWarning) => {
    dispatch({
      time: warning.time,
      selectedPlayer: isNumber(warning.player) ? warning.player : undefined,
      selectedAction: isNumber(warning.action) ? warning.action : undefined,
      showWarnings: false,
    });
  };

  return (
    <Drawer anchor="right" variant="temporary" open={state.showWarnings} onClose={() => dispatch({ showWarnings: false })}>
      <Stack sx={{ p: 4 }} spacing={2}>
        <Typography
          variant="h5"
          sx={{
            color: theme.palette.dsBase.v200,
            paddingBottom: theme.spacing(1),
            width: "250px",
          }}
        >
          {i18n("builder.warnings.title")}
        </Typography>
        {state.warnings.map((warning, index) => (
          <Stack key={index} direction="row" alignItems="flex-start" spacing={1}>
            <Typography
              variant="body2"
              onClick={() => handleWarningClick(warning)}
              sx={{
                fontWeight: 500,
                maxWidth: warning.fixAction ? "200px" : "250px",
                cursor: "pointer",
                color: warning.severity === WarningSeverity.Critical ? theme.palette.dsDestructive.v300 : theme.palette.dsWarning.v300,
                flex: 1,
              }}
            >
              {warning.message}
            </Typography>
            {warning.fixAction && (
              <BybButton
                variant="text"
                size="small"
                color={ButtonColor.SECONDARY}
                onClick={() => warning.fixAction?.()}
                sx={{
                  minWidth: "40px",
                  flexShrink: 0,
                }}
                aria-label={i18n("builder.physics.button.fix")}
              >
                {i18n("builder.physics.button.fix")}
              </BybButton>
            )}
          </Stack>
        ))}
      </Stack>
    </Drawer>
  );
};

export default WarningList;
